import { HttpErrorResponse } from "@angular/common/http";

/**
 * Client contract mirroring the read-only IpActivityTimelineDto
 * (api/src/Riley.Jms.Application/Models/IpActivityTimelineDtos.cs, story 937). The timeline is a
 * most-recent-first list of major workflow status events; availability is gated so the panel can
 * render a graceful unavailable state.
 */
export interface IpActivityTimeline {
  incarceratedPersonId: number;
  events: IpActivityEvent[];
  dataAvailability: IpActivityTimelineDataAvailability;
}

export interface IpActivityEvent {
  eventTypeCode: string;
  label: string;
  occurredUtc: string;
  actorUserId?: number | null;
  sourceCode: string;
  reasonCode?: string | null;
}

export interface IpActivityTimelineDataAvailability {
  activity: boolean;
}

export interface IpActivityTimelineDisplayError {
  title: string;
  messages: string[];
  statusCode?: number;
}

export function formatActivityWhen(event: IpActivityEvent): string {
  const occurred = event.occurredUtc ? new Date(event.occurredUtc) : null;
  if (!occurred || Number.isNaN(occurred.getTime())) {
    return "Date unavailable";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(occurred);
}

export function formatActivityReason(code: string | null | undefined): string {
  const normalized = (code ?? "").trim();
  if (!normalized) {
    return "";
  }
  return normalized
    .split(/[_\-.\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function mapActivityTimelineError(error: unknown): IpActivityTimelineDisplayError {
  if (error instanceof HttpErrorResponse) {
    const statusCode = error.status;
    if (statusCode === 401) {
      return { title: "Sign-in required", messages: ["Your session is no longer authenticated."], statusCode };
    }
    if (statusCode === 403) {
      return { title: "Access denied", messages: ["You do not have permission to view this activity."], statusCode };
    }
    return {
      title: "Activity unavailable",
      messages: ["The activity timeline could not be loaded."],
      statusCode,
    };
  }
  return { title: "Unexpected error", messages: ["The activity timeline could not be loaded."] };
}
